import {
  PaymentFrequencyType,
  PaymentPlanType,
} from 'QuoteApp/services/endpoints/GetPaymentDetails';
import {
  findPreferredInFullPaymentPlan,
  findPreferredMonthlyPaymentPlan,
  findPreferredSemiMonthlyPaymentPlan,
  getPaymentPlansByType,
} from 'QuoteApp/services/endpoints/GetPaymentDetails/utils';

const orderPaymentPlansBySelector = (
  paymentPlans: PaymentPlanType[],
  paymentFrequencies: PaymentFrequencyType[],
  preferredSelector: (plans: PaymentPlanType[]) => PaymentPlanType | undefined
): PaymentFrequencyType[] => {
  if (paymentPlans.length === 0) return [];
  if (paymentPlans.length === 1) {
    const freq = paymentFrequencies.find(
      f => f.paymentPlanId === paymentPlans[0].identifier
    );
    return freq ? [freq] : [];
  }

  // Obtaining the preferred plan
  const preferredPlan = preferredSelector(paymentPlans);

  if (!preferredPlan) {
    // Fallback: if there is no preferred plan, return the payment frequencies in the order they appear in the list
    return paymentPlans
      .map(plan =>
        paymentFrequencies.find(f => f.paymentPlanId === plan.identifier)
      )
      .filter((f): f is PaymentFrequencyType => !!f);
  }
  const preferredFreq = paymentFrequencies.find(
    f => f.paymentPlanId === preferredPlan.identifier
  );

  // Remove the preferred plan instance from the group.
  const remainingGroup = paymentPlans.filter(
    plan => plan.identifier !== preferredPlan.identifier
  );

  // Recursive ordering of the remaining group
  const orderedRemaining = orderPaymentPlansBySelector(
    remainingGroup,
    paymentFrequencies,
    preferredSelector
  );

  return preferredFreq
    ? [preferredFreq, ...orderedRemaining]
    : orderedRemaining;
};

export const getOrderedPaymentFrequencies = (
  paymentFrequencies: PaymentFrequencyType[],
  paymentPlans: PaymentPlanType[]
): { preferred: PaymentFrequencyType[]; remaining: PaymentFrequencyType[] } => {
  const monthlyPlans = getPaymentPlansByType(paymentPlans, 'monthly');
  const semiMonthlyPlans = getPaymentPlansByType(paymentPlans, 'semi_monthly');
  const inFullPlans = getPaymentPlansByType(paymentPlans, 'in_full');

  // Grouping payment plans by type
  const monthlyOrdered = orderPaymentPlansBySelector(
    monthlyPlans,
    paymentFrequencies,
    findPreferredMonthlyPaymentPlan
  );
  const semiOrdered = orderPaymentPlansBySelector(
    semiMonthlyPlans,
    paymentFrequencies,
    findPreferredSemiMonthlyPaymentPlan
  );
  const inFullOrdered = orderPaymentPlansBySelector(
    inFullPlans,
    paymentFrequencies,
    findPreferredInFullPaymentPlan
  );

  // Extracting preferred (the first element of each group, if it exists)
  const preferred: PaymentFrequencyType[] = [];
  if (monthlyOrdered.length > 0) {
    preferred.push(monthlyOrdered[0]);
  }
  if (semiOrdered.length > 0) {
    preferred.push(semiOrdered[0]);
  }
  if (inFullOrdered.length > 0) {
    preferred.push(inFullOrdered[0]);
  }

  // The remaining are the rest of each group
  const remaining: PaymentFrequencyType[] = [
    ...monthlyOrdered.slice(1),
    ...semiOrdered.slice(1),
    ...inFullOrdered.slice(1),
  ];

  return { preferred, remaining };
};
